import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { donationAPI } from '../../services/api';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import './Donate.css';

const Donate = () => {
    useScrollAnimation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        amount: '',
        message: '',
        isAnonymous: false
    });
    const [submitting, setSubmitting] = useState(false);

    const presetAmounts = [25, 50, 100, 250];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const selectAmount = (amount) => {
        setFormData({ ...formData, amount: amount.toString() });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.amount || parseFloat(formData.amount) < 1) {
            toast.error('Please enter a valid amount');
            return;
        }

        setSubmitting(true);

        try {
            const response = await donationAPI.create({
                ...formData,
                amount: parseFloat(formData.amount)
            });
            
            if (response.data.success) {
                toast.success(response.data.message);
                setFormData({ name: '', email: '', amount: '', message: '', isAnonymous: false });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error processing donation');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="donate-page">
            <div className="donate-hero">
                <div className="container">
                    <h1>Make a Difference Today</h1>
                    <p>Your contribution helps us continue our mission to serve communities in need</p>
                </div>
            </div>

            <div className="container">
                <div className="donate-content">
                    <div className="donate-form-container scroll-animate">
                        <h2>Make Your Donation</h2>
                        
                        <div className="donation-amounts">
                            {presetAmounts.map(amount => (
                                <button
                                    key={amount}
                                    type="button"
                                    className={`amount-btn ${formData.amount === amount.toString() ? 'active' : ''}`}
                                    onClick={() => selectAmount(amount)}
                                >
                                    ${amount}
                                </button>
                            ))}
                        </div>

                        <form className="donation-form" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label>Custom Amount ($) *</label>
                                <input 
                                    type="number" 
                                    name="amount"
                                    value={formData.amount}
                                    onChange={handleChange}
                                    placeholder="Enter amount"
                                    min="1"
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label>Full Name *</label>
                                <input 
                                    type="text" 
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label>Email Address *</label>
                                <input 
                                    type="email" 
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label>Message (Optional)</label>
                                <textarea 
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows="3"
                                    placeholder="Leave a message..."
                                ></textarea>
                            </div>

                            <div className="form-group checkbox">
                                <label>
                                    <input 
                                        type="checkbox" 
                                        name="isAnonymous"
                                        checked={formData.isAnonymous}
                                        onChange={handleChange}
                                    />
                                    <span>Make this donation anonymous</span>
                                </label>
                            </div>

                            <button type="submit" className="btn btn-success btn-block" disabled={submitting}>
                                {submitting ? 'Processing...' : `Donate $${formData.amount || '0'}`}
                            </button>
                        </form>
                    </div>

                    <div className="donate-info">
                        <div className="info-card scroll-animate">
                            <i className="fas fa-shield-alt"></i>
                            <h3>Secure Donation</h3>
                            <p>Your donation is secure and encrypted</p>
                        </div>
                        <div className="info-card scroll-animate">
                            <i className="fas fa-heart"></i>
                            <h3>100% Impact</h3>
                            <p>Every dollar goes directly to our programs</p>
                        </div>
                        <div className="info-card scroll-animate">
                            <i className="fas fa-file-invoice-dollar"></i>
                            <h3>Tax Deductible</h3>
                            <p>Donations are tax-deductible</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Donate;
